import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  HStack,
  Input,
  PinInput,
  PinInputField,
  VStack,
  Text,
  useToast,
  useColorModeValue,
  useBreakpointValue,
} from "@chakra-ui/react";
import API from "../context/API";
import axios from "axios";

const OtpPhoneInput = ({ setIsLoggedIn, phone, role }) => {
  const [otp, setOtp] = useState("");
  const [isResendDisabled, setIsResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [otpRequestCount, setOtpRequestCount] = useState(0);
  const toast = useToast();

  // Add ALL color mode values at the top level - no hooks inside callbacks!
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const titleColor = useColorModeValue("teal.600", "teal.400");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const cardBgColor = useColorModeValue("white", "gray.700");
  const inputBgColor = useColorModeValue("gray.50", "gray.600");
  const pinFieldBgColor = useColorModeValue("white", "gray.600");
  const hoverBgColor = useColorModeValue("teal.50", "teal.900");

  // Responsive values
  const pinSize = useBreakpointValue({ base: "md", md: "lg" });
  const cardWidth = useBreakpointValue({ base: "95%", sm: "90%", md: "400px" });
  const cardPadding = useBreakpointValue({ base: 3, sm: 4, md: 6 });
  const spacingValue = useBreakpointValue({ base: 6, md: 8 });
  const buttonSize = useBreakpointValue({ base: "md", md: "lg" });

  const MAX_OTP_REQUESTS = 5;
  const RESEND_COOLDOWN = 300; // 5 minutes in seconds
  const RESET_TIME = 3600; // 1 hour in seconds

  // Debug: Log the phone prop to see what's being received
  useEffect(() => {
    console.log("OTPInput received phone:", phone);
    console.log("OTPInput received role:", role);
  }, [phone, role]);

  useEffect(() => {
    // On initial render, check if there's an existing cooldown
    const storedCooldown = localStorage.getItem("resendCooldown");
    const storedRequestCount = localStorage.getItem("otpRequestCount");

    if (storedCooldown && storedRequestCount) {
      const remainingTime = Math.max(0, RESEND_COOLDOWN - (Date.now() - storedCooldown) / 1000);
      setOtpRequestCount(parseInt(storedRequestCount, 10));
      if (remainingTime > 0) {
        setResendTimer(remainingTime);
        setIsResendDisabled(true);
      }
    }
  }, []);

  useEffect(() => {
    // Handle resend timer countdown
    let timer;
    if (resendTimer > 0) {
      timer = setInterval(() => {
        setResendTimer((prev) => {
          if (prev <= 1) {
            setIsResendDisabled(false);
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [resendTimer]);

  useEffect(() => {
    // Reset OTP request count every hour
    const resetInterval = setInterval(() => {
      setOtpRequestCount(0);
      localStorage.removeItem("otpRequestCount");
    }, RESET_TIME * 1000);

    return () => clearInterval(resetInterval);
  }, []);

  const handleOtpChange = (value) => {
    setOtp(value);
  };

  const handleVerify = async () => {
    try {
      // Validate phone number before proceeding
      if (!phone || phone.trim() === "") {
        toast({
          title: "Phone Number Missing",
          description: "Phone number is required for OTP verification.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        console.error("Phone number is missing or empty:", phone);
        return;
      }

      if (otp.length < 6) {
        toast({
          title: "Invalid OTP",
          description: "Please enter a 6-digit OTP.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      console.log("Sending OTP verification request:", { phone: phone.trim(), otp });

      const res = await API.post(`/merchant/verify-otp`, {
        phone: phone.trim(), // Ensure phone is trimmed and not empty
        otp,
      });

      console.log("OTP verification response:", res);

      if (res.data.status === 200) {
        toast({
          title: "Verification Successful",
          description: "Your phone number and OTP have been verified.",
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // CRITICAL: Set authentication data BEFORE redirect
        const token = res.data.data.jwt;
        const userData = res.data.data;
        
        // Set all required data in localStorage
        localStorage.setItem("access_token", token);
        localStorage.setItem("user", JSON.stringify(userData));
        localStorage.setItem("role", role);
        
        // Set axios default header
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
        
        // Update local state
        setIsLoggedIn(true);
        
        setTimeout(() => {
          // Now redirect with the token
          const currentOrigin = window.location.origin;
          window.location.href = `${currentOrigin}/dashboard?access_token=${token}&role=${role}`;
        }, 1500);
        
        setOtpRequestCount(0);
        localStorage.removeItem("otpRequestCount");
      } else {
        toast({
          title: "Wrong OTP",
          description: "The OTP you entered is incorrect. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      console.error("Error response:", error.response?.data);
      
      // Show more specific error messages
      let errorMessage = "An error occurred while verifying the OTP. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = `Error: ${error.response.data.error}`;
      }
      
      toast({
        title: "Verification Failed",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleResendOtp = async () => {
    // Validate phone number before resending
    if (!phone || phone.trim() === "") {
      toast({
        title: "Phone Number Missing",
        description: "Phone number is required to resend OTP.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      console.error("Phone number is missing or empty:", phone);
      return;
    }

    if (otpRequestCount >= MAX_OTP_REQUESTS) {
      toast({
        title: "Too Many Requests",
        description: `You have reached the maximum of ${MAX_OTP_REQUESTS} requests. Please wait 5 minutes.`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setResendTimer(RESEND_COOLDOWN);
      setIsResendDisabled(true);
      localStorage.setItem("resendCooldown", Date.now());
      return;
    }

    try {
      console.log("Sending resend OTP request:", { phone: phone.trim() });
      
      await API.post(`/merchant/resend-otp`, { 
        phone: phone.trim() // Ensure phone is trimmed and not empty
      });
      
      setOtpRequestCount((prev) => {
        const newCount = prev + 1;
        localStorage.setItem("otpRequestCount", newCount);
        return newCount;
      });
      setResendTimer(RESEND_COOLDOWN);
      setIsResendDisabled(true);
      localStorage.setItem("resendCooldown", Date.now());
      toast({
        title: "OTP Resent",
        description: "A new OTP has been sent to your phone number.",
        status: "info",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error resending OTP:", error);
      console.error("Error response:", error.response?.data);
      
      let errorMessage = "An error occurred while resending the OTP. Please try again.";
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMessage = `Error: ${error.response.data.error}`;
      }
      
      toast({
        title: "Resend Failed",
        description: errorMessage,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Show error if phone is missing
  if (!phone || phone.trim() === "") {
    return (
      <Box
        p={cardPadding}
        maxWidth="100%"
        mx="auto"
        display="flex"
        height="100vh"
        justifyContent="center"
        alignItems="center"
        bg={bgColor}
      >
        <Box
          p={cardPadding}
          width={cardWidth}
          mx="auto"
          boxShadow="xl"
          borderRadius="xl"
          bg={cardBgColor}
          textAlign="center"
          border="1px solid"
          borderColor={borderColor}
        >
          <VStack spacing={6} align="stretch">
            <Text 
              fontSize={{ base: "xl", md: "2xl" }} 
              fontWeight="bold" 
              color="red.500"
              mb={2}
            >
              Phone Number Missing
            </Text>
            <Text fontSize="md" color={textColor}>
              Phone number is required for OTP verification. Please go back to login and try again.
            </Text>
            <Button 
              colorScheme="blue" 
              onClick={() => window.history.back()} 
              isFullWidth 
              size="lg"
              borderRadius="lg"
            >
              Go Back to Login
            </Button>
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      p={{ base: 2, sm: cardPadding }}
      maxWidth="100%"
      mx="auto"
      display="flex"
      height="100vh"
      justifyContent="center"
      alignItems="center"
      bg={bgColor}
    >
      <Box
        p={cardPadding}
        width={cardWidth}
        mx="auto"
        boxShadow={{ base: "lg", md: "xl" }}
        borderRadius={{ base: "lg", md: "xl" }}
        bg={cardBgColor}
        textAlign="center"
        border="1px solid"
        borderColor={borderColor}
        maxH={{ base: "90vh", md: "auto" }}
        overflowY="auto"
      >
        <VStack spacing={spacingValue} align="stretch">
          {/* Title */}
          <Text 
            fontSize={{ base: "xl", md: "2xl" }} 
            fontWeight="bold" 
            color={titleColor}
            mb={2}
          >
            Verify Your Phone Number
          </Text>

          {/* Phone Number Display */}
          <Box>
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color={textColor} 
              mb={2} 
              opacity={0.8}
            >
              We've sent a code to:
            </Text>
            <Input
              value={`${"*".repeat(Math.max(0, phone?.length - 4))}${phone?.slice(-4) || ""}`}
              readOnly
              focusBorderColor="teal.500"
              variant="filled"
              size={{ base: "md", md: "lg" }}
              borderRadius="lg"
              textAlign="center"
              bg={inputBgColor}
              color={textColor}
              fontWeight="medium"
              fontSize={{ base: "md", md: "lg" }}
            />
          </Box>

          {/* OTP Input */}
          <Box>
            <Text 
              fontSize={{ base: "xs", md: "sm" }} 
              color={textColor} 
              mb={{ base: 3, md: 4 }} 
              opacity={0.8}
            >
              Enter the 6-digit code
            </Text>
            <HStack 
              justifyContent="center" 
              spacing={{ base: 2, md: 3 }}
              flexWrap="wrap"
            >
              <PinInput
                onChange={handleOtpChange}
                value={otp || ""}
                focusBorderColor="teal.500"
                size={pinSize}
                otp
                autoFocus
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <PinInputField 
                    key={index} 
                    bg={pinFieldBgColor}
                    color={textColor}
                    borderColor={borderColor}
                    borderWidth="2px"
                    borderRadius="lg"
                    fontSize={{ base: "md", md: "lg", lg: "xl" }}
                    fontWeight="bold"
                    width={{ base: "40px", sm: "45px", md: "50px" }}
                    height={{ base: "40px", sm: "45px", md: "50px" }}
                    _focus={{
                      borderColor: "teal.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-teal-500)",
                    }}
                    _hover={{
                      borderColor: "teal.400",
                    }}
                  />
                ))}
              </PinInput>
            </HStack>
          </Box>

          {/* Resend OTP */}
          <Box>
            <Button
              colorScheme="blue"
              variant="ghost"
              onClick={handleResendOtp}
              isDisabled={isResendDisabled}
              color={textColor}
              size={{ base: "xs", md: "sm" }}
              fontSize={{ base: "xs", md: "sm" }}
              _hover={{
                bg: hoverBgColor,
              }}
            >
              {isResendDisabled 
                ? `Resend OTP in ${Math.ceil(resendTimer)}s` 
                : "Resend OTP"
              }
            </Button>
          </Box>

          {/* Verify Button */}
          <Button 
            colorScheme="blue" 
            onClick={handleVerify} 
            isFullWidth 
            size={buttonSize}
            borderRadius="lg"
            fontSize={{ base: "md", md: "lg" }}
            fontWeight="semibold"
            py={{ base: 4, md: 6 }}
            _hover={{
              transform: "translateY(-1px)",
              boxShadow: "lg",
            }}
            _active={{
              transform: "translateY(0)",
            }}
          >
            Verify
          </Button>

          {/* Help Text */}
          <Text 
            fontSize={{ base: "2xs", md: "xs" }} 
            color={textColor} 
            opacity={0.6}
            textAlign="center"
            px={2}
          >
            Didn't receive the code? Check your spam folder or try resending.
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default OtpPhoneInput;
